import React, {useEffect, useMemo, useState} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {GlobalToaster} from './UI/GlobalToaster';
import {AppSidebar} from './Layout/AppSidebar';
import {AppHeader} from './Layout/AppHeader';
import {BottomNav} from './Layout/BottomNav';
import {useNotifications} from '../hooks/useNotifications';

/**
 * Khung màn hình của chủ nhà / quản lý.
 *
 * Bố cục kiểu bảng điều khiển: sidebar cố định bên trái từ `lg` trở lên, thu
 * thành drawer ở màn nhỏ; header dính trên cùng gom tìm kiếm, chuông việc đang
 * chờ, đổi sáng/tối và menu tài khoản.
 *
 * Ngưỡng là `lg` chứ không phải `md`: sidebar rộng 17rem, để lại chưa tới 32rem
 * cho nội dung trên máy tính bảng dọc - các bảng và lưới thẻ ở /diennuoc bị bóp
 * đến mức khó dùng.
 */
export const AppLayout: React.FC = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const location = useLocation();
    const {notifications} = useNotifications();

    // Đổi trang thì đóng drawer - nếu không, bấm một mục menu xong drawer vẫn
    // che mất trang vừa mở.
    useEffect(() => setIsSidebarOpen(false), [location.pathname]);

    // Chấm đỏ trên mục menu: gom thông báo theo đích đến của chúng.
    // Đích đến trỏ vào ĐÚNG một phòng (/diennuoc/201, /phong/201) và còn có
    // thể mang tham số, mà chấm đỏ thì gắn vào MỤC MENU - nên cắt hết về đoạn
    // đầu, nếu không key chẳng khớp mục nào và chấm đỏ biến mất.
    const badges = useMemo(() => {
        return notifications.reduce<Record<string, number>>((acc, notification) => {
            if (!notification.to) return acc;
            const [, section] = notification.to.split('?')[0].split('/');
            if (!section) return acc;
            acc[`/${section}`] = (acc[`/${section}`] ?? 0) + 1;
            return acc;
        }, {});
    }, [notifications]);

    return (
        <div className="min-h-app lg-app-bg">
            <AppSidebar
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                badges={badges}
            />

            {/* Chừa chỗ cho thanh điều hướng đáy - nó `fixed` nên không tự đẩy
                nội dung lên, thiếu padding là nó che mất phần cuối trang.
                `.pb-bottom-nav` lấy chiều cao THẬT do BottomNav tự đo và ghi ra
                biến CSS, chứ không đoán bằng một con số cứng: trên iPhone thanh
                còn cộng vùng an toàn của vạch Home, và cỡ chữ lớn làm nó cao
                thêm nữa - xem index.css. */}
            <div className="lg:ml-[17rem] min-h-app flex flex-col pb-bottom-nav">
                <AppHeader
                    onOpenSidebar={() => setIsSidebarOpen(true)}
                    notifications={notifications}
                />

                {/* content-clip-x: lưới an toàn cho bề ngang khi người dùng bật
                    cỡ chữ lớn - xem index.css. Thật sự sửa là ở từng khối, đây
                    chỉ để một chỗ sót không kéo lệch cả trang. */}
                <main className="flex-1 content-clip-x">
                    <Outlet/>
                </main>

                {/* Dòng bản quyền chỉ hiện trên máy tính. Trên điện thoại nó nằm
                    ngay trên thanh điều hướng đáy, ăn mất một dải màn hình vốn
                    đã hẹp để nói một điều không ai cần khi đang ghi số điện. */}
                <footer className="hidden lg:block py-4 px-6 text-center text-sm text-slate-500 dark:text-slate-400
                    bg-white/30 dark:bg-white/5 sm:backdrop-blur border-t border-white/40 dark:border-white/10">
                    © 2025 Bùi Tiến Tuân. All rights reserved.
                </footer>
            </div>

            <BottomNav badges={badges}/>

            <GlobalToaster/>
        </div>
    );
};
